import { useTranslation } from "react-i18next";
import { LeadForm } from "./LeadForm";
import { Star } from "lucide-react";

export const HeroSection = () => {
  const { t, i18n } = useTranslation();
  return (
    <section className="min-h-screen relative overflow-hidden bg-gradient-to-b from-blue-50 to-white pt-6">
      {/* Decorative Circle - Top Left */}
      <img
        src="/Circle.png"
        alt="Retail Icon"
        className="w-[110px] h-[110px] md:w-[220px] md:h-[220px] absolute opacity-20 
             -top-[45px] md:-top-[91px] -left-[50px] md:-left-[100px] z-0 pointer-events-none"
      />

      {/* Decorative Circle - Top Right */}
      <img
        src="/Circle.png"
        alt="Retail Icon"
        className="w-[190px] h-[190px] md:w-[380px] md:h-[380px] absolute opacity-20 
             -top-[23px] md:-top-[47px] -right-[102px] md:-right-[205px] z-0 pointer-events-none"
      />

      {/* Decorative Circle - Bottom Right */}
      <img
        src="/Circle.png"
        alt="Retail Icon"
        className="w-[200px] h-[200px] md:w-[400px] md:h-[400px] absolute opacity-20 
             -bottom-[75px] md:-bottom-[150px] -right-[125px] md:-right-[250px] z-0 pointer-events-none"
      />
      <div className="md:hidden max-w-[63rem] mx-auto text-center px-2 mb-10" id="lead-form">
        <div className="bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 border border-primary/10">
          <LeadForm />
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 text-xs sm:text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              ✓ {t("No credit card required")}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-2">
              ✓ {t("Free setup assistance")}
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-2">
              ✓ {t("Start selling in 10 minutes")}
            </span>
          </div>
        </div>
      </div>
      <div className="relative container mx-auto pb-4 py-6 md:py-10 pt-0 px-4">
        {/* Row 1: Hero Content + Image */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center mb-6 md:mb-8">
          {/* Left Side - Hero Text Content */}
          <div className="space-y-3 md:space-y-4 text-center lg:text-left animate-fade-in">
            {/* Badge */}
            <div className={"flex justify-start"}>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm font-medium text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                {t("Welcome to the Future of Retail")} 🛍️
              </div>
            </div>

            {/* Main Heading */}
            <h1
              className={`text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight md:leading-[55px] max-w-[700px] mx-auto lg:mx-0 ${i18n.language === "ar" ? "text-right" : "text-left"
                }`}
            >
              {t("The Smartest Cashier & Store Management System")}
            </h1>

            {/* Subheading */}
            <h1
              className={`text-xl sm:text-2xl md:text-4xl font-semibold text-primary ${i18n.language === "ar" ? "text-right" : "text-left"
                }`}
            >
              {t("Built for Egyptian Retailers")}
            </h1>

            {/* Description */}
            <p
              className={`text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 ${i18n.language === "ar" ? "text-right" : "text-left"
                }`}
            >
              {t("Start your store in just")}{" "}
              <span className="font-bold text-foreground">
                {t("10 minutes")}
              </span>
              .
            </p>

            {/* CTA Message */}
            <p
              className={`hidden md:block text-base sm:text-lg font-semibold text-foreground pt-2 md:pt-4 ${i18n.language === "ar" ? "text-right" : "text-left"
                }`}
            >
              {t("To unlock")}{" "}
              <span className="text-primary">{t("FREE deals")}</span>{" "}
              {t("& exclusive benefits, fill the form now.")}
            </p>

            {/* Trust Indicators */}
            <div className="hidden sm:flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 md:gap-6">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                  ))}
                </div>
                <span className="font-semibold text-foreground">4.8/5</span>
              </div>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <p className="text-muted-foreground font-medium">
                {t("Used by")}{" "}
                <span className="font-bold text-primary">1000+</span>{" "}
                {t("stores")}
              </p>
            </div>
          </div>

          {/* Right Side - Product Image */}
          <div className="flex justify-center items-center animate-scale-in w-full max-w-[680px] mx-auto">
            <img
              id="hero-image"
              src={require("../../assets/Images/LandingPageBannerImages.png")}
              alt="Hisabak Devices - POS System"
              className="w-full h-auto z-[1]"
            />
            <img
              src="/blueBack.png"
              alt="Retail Icon"
              className="absolute w-full max-w-[300px] sm:max-w-[350px] md:max-w-[450px] z-0"
            />
          </div>
        </div>

        {/* Row 2: Centered Form Section */}
        <div className="hidden md:flex justify-center max-w-[63rem] mx-auto text-center px-2" id="lead-form">
          <div className="bg-white rounded-2xl md:rounded-3xl p-3 md:p-4 border border-primary/10">
            <LeadForm />
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                ✓ {t("No credit card required")}
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-2">
                ✓ {t("Free setup assistance")}
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-2">
                ✓ {t("Start selling in 10 minutes")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
